import type { Analytics } from 'firebase/analytics'
import { info } from '@/lib/logger'

const isDevEnv = process.env.NODE_ENV === 'development'

interface FirebaseAnalytics {
  analytics: Analytics
  logEvent: (
    analytics: Analytics,
    eventName: string,
    eventParams?: Record<string, unknown>
  ) => void
  setUserId: (analytics: Analytics, id: string | null) => void
}

let _firebase: FirebaseAnalytics | null = null

const _loadFirebaseAnalytics = async (): Promise<FirebaseAnalytics> => {
  if (_firebase) {
    return _firebase
  }
  const {
    getAnalytics,
    logEvent: firebaseLogEvent,
    setUserId: firebaseSetUserId,
  } = await import('firebase/analytics')
  _firebase = {
    analytics: getAnalytics(),
    logEvent: firebaseLogEvent,
    setUserId: firebaseSetUserId,
  }
  return _firebase
}

/**
 * Send the userId to firebase analytics to track users and their actions
 */
export const sendUserIdToFirebase = async (userId: string): Promise<void> => {
  try {
    const { analytics, setUserId: firebaseSetUserId } =
      await _loadFirebaseAnalytics()
    firebaseSetUserId(analytics, userId)
  } catch (error) {
    // error loading firebase, likely blocked using some kind of tracker blocker
    const message = error instanceof Error ? error.message : String(error)
    info('Firebase Logging', 'Failed to set user ID:', message)
  }
}

/**
 * Log an event with firebase analytics
 *
 * @param name - Event name
 * @param props - Event properties
 */
export const log = async (
  name: string,
  props: Record<string, unknown> = {}
): Promise<void> => {
  try {
    const { analytics, logEvent: firebaseLogEvent } =
      await _loadFirebaseAnalytics()

    const eventProps = { ...props, app: 'fish' }

    if (!isDevEnv) {
      firebaseLogEvent(analytics, name, eventProps)
    }

    info('Firebase Logging', 'log event to Firebase:', name, eventProps)
  } catch (e) {
    // error loading firebase, likely blocked using some kind of tracker blocker
  }
}
